import { useTranslation } from "react-i18next";
import type { Author, StructuredOutput, ThreadEntry } from "../../../api/threads/liveMessages";
import RunMarkdown from "./RunMarkdown";
import { agentMemorySave } from "../../memories/agentSave";
import MemorySavedBody from "../../memories/MemorySavedBody";
import { stepKindText } from "../../workflows/inputCards/typeWords";
import { useThreadContext } from "../page/threadPageContext";
import EntryFrame from "./EntryFrame";
import { otherPersonsAgent, type AgentTurn, type TurnStep } from "./logBlocks";
import { HumanInputBody, HumanStepRequestedBody } from "./HumanStepEntries";
import LongTextFold from "./LongTextFold";
import { startTextInputs } from "./startInputs";
import {
  ForkOfferedBody,
  ForkOriginBody,
  HostChangedBody,
  HostingChangedBody,
  OutcomeNoticeBody,
  ProblemBody,
  SettingsChangedBody,
  ThreadStartedBody,
  WorkflowEventBody,
} from "./LogEventEntries";
import { ToolTurnFinishedBody, ToolTurnRequestedBody } from "./ToolTurnEntries";
import { StepOutputBody, ToolCallBody, TurnFinishedBody, TurnRequestedBody } from "./TurnEntries";

/**
 * One entry, drawn by its kind (threads.md T-2). Messages and agent answers are cards, each
 * with who wrote or answered it (F70); records of what happened to the Thread are single
 * lines; agent text is Markdown without raw HTML (`RunMarkdown`). A message that waits
 * for its sender's own agent says so (R2).
 */
interface ThreadEntryViewProps {
  entry: ThreadEntry;
  turnStep: TurnStep | null;
  /** The agent turn that wrote this answer or output; `null` for any other entry. */
  agentTurn?: AgentTurn | null;
  /** For an answer to a review: the result it reviewed, if its request is loaded. */
  reviewed?: StructuredOutput;
  request?: ThreadEntry["payload"];
}

export default function ThreadEntryView({ entry, turnStep, agentTurn = null, reviewed, request }: ThreadEntryViewProps) {
  const { t } = useTranslation();
  const { nameOf, stepName, overview, currentUserId, workflowInputsOf } = useThreadContext();
  const author = authorLabel(entry.author, nameOf, t("threads.entry.agent"), t("threads.entry.system"));
  const payload = entry.payload;
  const agentOwner = otherPersonsAgent(agentTurn, currentUserId);

  switch (payload.kind) {
    case "message":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="card" tone="person">
          <RunMarkdown content={payload.text} className="app-markdown text-sm" />
          {overview.waitingForSender.includes(entry.number) ? (
            <p className="app-text-muted text-xs">
              {entry.author.kind === "user" && entry.author.user_id === currentUserId ? t("threads.entry.waitsForYou") : t("threads.entry.waitsForSender", { name: author })}
            </p>
          ) : null}
        </EntryFrame>
      );
    case "turn_finished":
      return (
        <EntryFrame
          entry={entry}
          authorLabel={agentOwner === null ? author : t("threads.entry.agentOf", { name: nameOf(agentOwner) })}
          layout="card"
          tone="agent"
          agentTurn={agentTurn}
        >
          <TurnFinishedBody entry={entry} payload={payload} turnStep={turnStep} />
        </EntryFrame>
      );
    case "human_step_requested":
      return (
        <EntryFrame entry={entry} authorLabel={t("threads.humanStep.title", { kind: stepKindText("human", t), step: stepName(payload.step_key, payload.step) })} layout="card" tone="system">
          <HumanStepRequestedBody entry={entry} payload={payload} />
        </EntryFrame>
      );
    case "human_input":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="card" tone="person">
          <HumanInputBody payload={payload} reviewed={reviewed} request={request} />
        </EntryFrame>
      );
    case "step_output":
      return (
        <EntryFrame
          entry={entry}
          authorLabel={turnStep === null ? t("threads.output.title") : t("threads.output.stepResult", { step: stepName(turnStep.stepKey, turnStep.step) })}
          layout="card"
          tone="agent"
          agentTurn={agentTurn}
        >
          <StepOutputBody payload={payload} entry={entry.number} />
        </EntryFrame>
      );
    case "problem":
      return (
        <EntryFrame entry={entry} authorLabel={t("threads.events.problem")} layout="card" tone="system">
          <ProblemBody payload={payload} />
        </EntryFrame>
      );
    case "fork_offered":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="card" tone="system">
          <ForkOfferedBody entry={entry} payload={payload} />
        </EntryFrame>
      );
    case "tool_turn_finished":
      return (
        <EntryFrame entry={entry} authorLabel={t("threads.toolTurn.title")} layout="card" tone="agent">
          <ToolTurnFinishedBody payload={payload} />
        </EntryFrame>
      );
    case "tool_turn_requested":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <ToolTurnRequestedBody payload={payload} />
        </EntryFrame>
      );
    case "turn_requested":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <TurnRequestedBody payload={payload} />
        </EntryFrame>
      );
    case "tool_call": {
      const savedMemory = agentMemorySave(payload);
      return savedMemory === null ? (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <ToolCallBody payload={payload} threadId={entry.thread_id} />
        </EntryFrame>
      ) : (
        <EntryFrame entry={entry} authorLabel={author} layout="card" tone="system">
          <MemorySavedBody save={savedMemory} payload={payload} projectId={overview.projectId} />
        </EntryFrame>
      );
    }
    case "thread_started": {
      // What the starter wrote into the start form reads as their message, each text under its label (Johannes, 4 Nov
      // 15:36); a start nobody can be named for shows no name. A start without such text stays a line.
      const said = payload.workflow === null ? [] : startTextInputs(payload.input_values, workflowInputsOf(payload.workflow));
      if (said.length === 0) {
        return (
          <EntryFrame entry={entry} authorLabel={author} layout="line">
            <ThreadStartedBody payload={payload} />
          </EntryFrame>
        );
      }
      return (
        <EntryFrame entry={entry} authorLabel={entry.author.kind === "user" ? author : ""} layout="card" tone="person">
          <p className="app-text-muted text-xs">
            <ThreadStartedBody payload={payload} />
          </p>
          {said.map((input) => (
            <div key={input.destination} className="space-y-1">
              <p className="app-text-muted text-xs font-semibold">{input.label}</p>
              <LongTextFold text={input.text} show={(text) => <RunMarkdown content={text} className="app-markdown text-sm" />} />
            </div>
          ))}
        </EntryFrame>
      );
    }
    case "fork_origin":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <ForkOriginBody payload={payload} />
        </EntryFrame>
      );
    case "settings_changed":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          {author}: <SettingsChangedBody payload={payload} />
        </EntryFrame>
      );
    case "title_changed":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          {t("threads.events.titleChanged", { user: author, title: payload.title })}
        </EntryFrame>
      );
    case "workflow_event":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <WorkflowEventBody payload={payload} byRunner={entry.author.kind === "system"} />
        </EntryFrame>
      );
    case "outcome_notice":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <OutcomeNoticeBody payload={payload} />
        </EntryFrame>
      );
    case "host_changed":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <HostChangedBody payload={payload} />
        </EntryFrame>
      );
    case "hosting_changed":
      return (
        <EntryFrame entry={entry} authorLabel={author} layout="line">
          <HostingChangedBody payload={payload} />
        </EntryFrame>
      );
  }
}

function authorLabel(author: Author, nameOf: (userId: string) => string, agent: string, system: string): string {
  switch (author.kind) {
    case "user":
      return nameOf(author.user_id);
    case "agent":
      return agent;
    case "system":
      return system;
  }
}
